import React, { useCallback, useContext, useEffect, useState } from "react";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { Feature } from "geojson";

import { MapContext } from "fond/map/MapProvider";
import ToolbarToggleButton from "fond/map/Toolbar/ToolbarToggleButton";

interface PrioritizeButtonProps {
  prioritize: boolean;
}

const PrioritizeButton: React.FC<PrioritizeButtonProps> = ({ prioritize }: PrioritizeButtonProps) => {
  const { map, drawControl } = useContext(MapContext);
  const [isDisabled, setIsDisabled] = useState(true);

  /**
   * Monitor when the selectionchange event occurs so that we can disabled
   * this feature based on if a single Multipolygon is selected.
   */
  const handleOnSelect = useCallback(() => {
    const selectedFeatures = drawControl.current?.getSelected().features || [];

    // Confirm that the selected feature is a multiPolygon but also have multiple coordinates.
    if (selectedFeatures.length > 0) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleOnClick = () => {
    const selectedFeatures = drawControl.current?.getSelected().features || [];

    selectedFeatures?.forEach(({ properties }) => {
      const id = properties?.id;
      if (id && drawControl.current?.get(id)) {
        drawControl.current.setFeatureProperty(id, "excluded", (!prioritize).toString());
        // Add the feature to force a redraw
        drawControl.current.add(drawControl.current.get(id) as Feature);
      }
    });

    // Fire off a custom draw event that indicates that prioeritization has occurred.
    map?.fire("draw.prioritize", { type: "draw.prioritize", mode: "prioritize" });
  };

  const action = prioritize ? "Prioritize" : "Defer";

  return (
    <ToolbarToggleButton title={`${action}`} value={action} onClick={handleOnClick} disabled={isDisabled}>
      {prioritize ? <CheckCircle color={isDisabled ? "disabled" : "success"} /> : <Cancel color={isDisabled ? "disabled" : "secondary"} />}
    </ToolbarToggleButton>
  );
};

export default PrioritizeButton;
